/* eslint-disable react-hooks/exhaustive-deps */
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Dispatch, FC, ReactElement, RefObject, SetStateAction } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { motion } from "framer-motion"
import includes from "lodash-es/includes"
import {
    type Location,
    type Params,
    type SetURLSearchParams,
    useLocation,
    useParams,
    useSearchParams
} from "react-router-dom"
import { useUnmount, useUpdate } from "react-use"

import { CoachBreadcrumbs } from "@/3514/components/coach/breadcrumbs/coach.breadcrumbs.component"
import { type TCoachBreadcrumb } from "@/3514/components/coach/breadcrumbs/coach.breadcrumbs.types"
import { HeaderMetaCoach } from "@/3514/components/shared/header-meta/header-meta.coach.component"
import { ERoute } from "@/3514/constants"
import {
    EWindowCustomEvent,
    type TScreenBreakpoints,
    type TUserState,
    useScreenBreakpoints,
    useUser,
    useWindowCustomEventListener
} from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import { getCurrentLegacyCoachCalendarRouteFromPathname, getCurrentRouteFromPathname, isUserCoach } from "@/3514/utils"
import { type IUseTranslation, type TUseLogoNavigation, useLogoNavigation, useTranslation } from "@/hooks"
import { CoachCalendarStepUrls } from "@/pages/coach-settings/constants"
import { LocalStorageServiceKeys, StorageService } from "@/services"
import ProductTypeLogo from "@/shared/ProductTypeLogo"
import { type TEmptyCallback } from "@/shared/types/functions"

import config from "./headers.config"
import Profile from "./headers.elements.profile"
import classes from "./headers.styles"
import {
    type TCoachHeaderGroupMetaState as TGroupState,
    type TCoachHeaderParticipantMetaState as TParticipantState
} from "./headers.types"

const storageService: StorageService = new StorageService()

const CoachHeader: FC = (): ReactElement => {
    const dropdownRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const { user }: TUserState = useUser()

    const { t }: IUseTranslation = useTranslation()

    const qc: QueryClient = useQueryClient()

    const triggerUpdate: TEmptyCallback = useUpdate()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    const { handleLogoNavigation }: TUseLogoNavigation = useLogoNavigation()

    const { pathname }: Location = useLocation()

    const { groupId: groupIdParam, participantId: participantIdParam }: Readonly<Params> = useParams()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const sessionIdParam: string = searchParams.get("sessionId")

    const currentRoute: ERoute = useMemo<ERoute>((): ERoute => getCurrentRouteFromPathname(pathname), [pathname])
    const currentLegacyRoute: CoachCalendarStepUrls = useMemo<CoachCalendarStepUrls>(
        (): CoachCalendarStepUrls => getCurrentLegacyCoachCalendarRouteFromPathname(pathname),
        [pathname]
    )

    async function onLogoutComplete(): Promise<void> {
        if (isUserCoach(user) && user.areSubUsersPresent)
            storageService.setItem<boolean>(LocalStorageServiceKeys.MainUserLoggedOut(), true)
    }

    const isMetaReadyRef: RefObject<boolean> = useRef<boolean>(false)

    const [groupMetaState, setGroupMetaState]: [TGroupState, Dispatch<SetStateAction<TGroupState>>] =
        useState<TGroupState>({
            groupName: "Group",
            cohortName: "Wiser Cohort"
        })

    const [participantMetaState, setParticipantMetaState]: [
        TParticipantState,
        Dispatch<SetStateAction<TParticipantState>>
    ] = useState<TParticipantState>({
        firstName: "John",
        lastName: "Doe",
        cohortName: "Cohort",
        photo: null
    })

    useEffect((): void => {
        startTransition((): void => {
            isMetaReadyRef.current = false
            triggerUpdate()
        })
    }, [groupIdParam, participantIdParam])

    useUnmount((): void => {
        startTransition((): void => {
            setGroupMetaState({
                groupName: "Group",
                cohortName: "Wiser Cohort"
            })
            setParticipantMetaState({
                firstName: "John",
                lastName: "Doe",
                cohortName: "Cohort",
                photo: null
            })
        })
    })

    const isHiddenRoute: boolean = useMemo<boolean>(
        (): boolean =>
            includes<ERoute>(
                [
                    ERoute.Coach_Dashboard,
                    ERoute.Coach_CalendarSetup,
                    ERoute.Coach_CalendarAvailability,
                    ERoute.Coach_CalendarAccounts,
                    ERoute.Coach_RelinkAccounts
                ],
                currentRoute
            ) || includes<CoachCalendarStepUrls>([CoachCalendarStepUrls.LINK_ACCOUNTS_STEP], currentLegacyRoute),
        [currentRoute, currentLegacyRoute]
    )

    const breadcrumbs: TCoachBreadcrumb[] = useMemo<TCoachBreadcrumb[]>(
        (): TCoachBreadcrumb[] =>
            config.renderCoachBreadcrumbs({
                groupId: Number(groupIdParam),
                sessionId: Number(sessionIdParam),
                participantId: Number(participantIdParam),
                groupName: groupMetaState.groupName,
                participantName: `${participantMetaState.firstName} ${participantMetaState.lastName}`,
                currentRoute,
                qc,
                t
            })[currentRoute],
        [
            groupIdParam,
            groupMetaState.groupName,
            participantIdParam,
            participantMetaState.firstName,
            participantMetaState.lastName,
            sessionIdParam,
            currentRoute
        ]
    )

    const groupListenerHandler: (e: CustomEvent<TGroupState>) => void = useCallback(
        (e: CustomEvent<TGroupState>): void => {
            function cb(): void {
                setGroupMetaState(e.detail)
                isMetaReadyRef.current = true
            }

            if (isMobile) cb()
            else startTransition((): void => cb())
        },
        [isMobile]
    )

    const groupParticipantListenerHandler: (e: CustomEvent<TParticipantState>) => void = useCallback(
        (e: CustomEvent<TParticipantState>): void => {
            function cb(): void {
                setParticipantMetaState(e.detail)
                isMetaReadyRef.current = true
            }

            if (isMobile) cb()
            else startTransition((): void => cb())
        },
        [isMobile]
    )

    const individualParticipantListenerHandler: (e: CustomEvent<TParticipantState>) => void = useCallback(
        (e: CustomEvent<TParticipantState>): void => {
            function cb(): void {
                setParticipantMetaState(e.detail)
                isMetaReadyRef.current = true
            }

            if (isMobile) cb()
            else startTransition((): void => cb())
        },
        [isMobile]
    )

    useWindowCustomEventListener(EWindowCustomEvent.CoachGroupMeta, groupListenerHandler)

    useWindowCustomEventListener(EWindowCustomEvent.CoachGroupParticipantMeta, groupParticipantListenerHandler)

    useWindowCustomEventListener(
        EWindowCustomEvent.CoachIndividualParticipantMeta,
        individualParticipantListenerHandler
    )

    return (
        <motion.header
            layout="preserve-aspect"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{
                duration: 0.3,
                delay: 0.05,
                type: "tween",
                ease: "easeInOut"
            }}
            className={classes.coach.header()}
        >
            <div className={classes.coach.headerContainer({ isJustifyEnd: isHiddenRoute && !isMobile })}>
                {!isMobile && (
                    <CoachBreadcrumbs isVisible={!isHiddenRoute} isReady={isMetaReadyRef.current} items={breadcrumbs} />
                )}

                {isMobile && <ProductTypeLogo handleLogoClick={handleLogoNavigation} />}

                <Profile<EUserRole.Coach>
                    role={EUserRole.Coach}
                    dropdownRef={dropdownRef}
                    onLogoutComplete={onLogoutComplete}
                />
            </div>

            {isMobile && (
                <CoachBreadcrumbs isVisible={!isHiddenRoute} isReady={isMetaReadyRef.current} items={breadcrumbs} />
            )}

            <HeaderMetaCoach
                isVisible={!isHiddenRoute}
                isReady={isMetaReadyRef.current}
                groupState={groupMetaState}
                participantState={participantMetaState}
            />
        </motion.header>
    )
}

CoachHeader.displayName = "CoachHeader"

export { CoachHeader }
